import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';

/**
 * Where a key moves the selection: arrows step (wrapping, direction-aware), Home / End jump.
 * Null when the key is not one of ours. Pure, so the keyboard contract is unit-tested.
 */
export function moveForKey(
  key: string,
  current: number,
  count: number,
  rtl: boolean,
): number | null {
  if (count === 0) return null;
  const forward = rtl ? 'ArrowLeft' : 'ArrowRight';
  const back = rtl ? 'ArrowRight' : 'ArrowLeft';
  const wrap = (i: number) => ((i % count) + count) % count;
  if (key === forward || key === 'ArrowDown') return wrap(current + 1);
  if (key === back || key === 'ArrowUp') return wrap(current - 1);
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  return null;
}

export interface Segment<T extends string> {
  value: T;
  label: string;
  icon?: ReactNode;
}

/**
 * The one segmented control (M5.2): a radiogroup whose segments are 44px or taller targets.
 * Arrow keys (and Home / End) move the selection; the selected segment is an elevated surface
 * with a 1px line and ink text, the others graphite; focus is the 3px TWO Blue ring. Neutral
 * in every use (sign-in method, Tasks segments, the language, the permission levels).
 */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  label,
  size = 'md',
}: {
  value: T;
  onChange: (value: T) => void;
  options: readonly Segment<T>[];
  label: string;
  /** md: 48px track (44px segments); sm: 44px track (40px segments, 44px hit area kept by padding). */
  size?: 'md' | 'sm' | undefined;
}) {
  const name = useId();
  const groupRef = useRef<HTMLDivElement>(null);

  const moveTo = (index: number) => {
    const next = options[(index + options.length) % options.length];
    if (!next) return;
    onChange(next.value);
    const input = groupRef.current?.querySelector<HTMLInputElement>(`input[value="${next.value}"]`);
    input?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const current = options.findIndex((o) => o.value === value);
    const rtl = (groupRef.current?.closest('[dir]')?.getAttribute('dir') ?? 'ltr') === 'rtl';
    const next = moveForKey(e.key, current, options.length, rtl);
    if (next === null) return;
    e.preventDefault();
    moveTo(next);
  };

  return (
    <fieldset className="m-0 border-0 p-0">
      <legend className="sr-only">{label}</legend>
      <div
        ref={groupRef}
        role="radiogroup"
        aria-label={label}
        onKeyDown={onKeyDown}
        className={[
          'segmented grid grid-flow-col auto-cols-fr gap-1 rounded-md border border-line bg-canvas p-0.5',
          size === 'sm' ? 'min-h-11' : 'min-h-12',
        ].join(' ')}
      >
        {options.map((opt) => {
          const selected = opt.value === value;
          return (
            <label
              key={opt.value}
              data-selected={selected || undefined}
              className={[
                'segment relative flex min-h-11 cursor-pointer select-none items-center justify-center gap-2 rounded-[calc(var(--radius-md)-2px)] px-3 text-sm font-medium',
                'transition-[background-color,color,box-shadow,border-color] duration-[var(--pa-dur-fast)] ease-[var(--pa-ease)]',
                selected
                  ? 'border border-line bg-surface text-ink shadow-sm dark:bg-elevated'
                  : 'border border-transparent text-ink-2 hover:text-ink',
                'has-[:focus-visible]:outline-[3px] has-[:focus-visible]:outline-focus has-[:focus-visible]:outline-offset-[-1px] has-[:focus-visible]:outline',
              ].join(' ')}
            >
              <input
                type="radio"
                name={name}
                value={opt.value}
                checked={selected}
                onChange={() => onChange(opt.value)}
                className="sr-only"
                // Roving focus: only the selected radio is in the tab order; arrows move it.
                tabIndex={selected ? 0 : -1}
              />
              {opt.icon}
              <span className="truncate">{opt.label}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
